// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

// ** Components
import Section from '@/components/common/Section';
import TextBlock from '@/components/common/TextBlock';
import BlocksContentRenderer, { type RendererOptions } from '@/components/strapi/BlocksContentRenderer';

// ** Utils
import { buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ** Types
import type { StrapiComponentProps } from '@/types/strapi';
import type { ComponentBlocksTextBlock as TextBlockType } from '@/types/graphql';

type ComponentBlocksTextBlockProps = StrapiComponentProps<TextBlockType>;

const nullToUndefined = <T,>(value: T | null | undefined): T | undefined => (value === null ? undefined : value);

const alignmentMap: Record<string, 'left' | 'center'> = {
	Default: 'left',
	Centered: 'center'
};

const ComponentBlocksTextBlock = ({
	containerSx,
	textBlockHeadline,
	textBlockAlignment,
	textBlockContent,
	textBlockCta
}: ComponentBlocksTextBlockProps) => {
	const theme = useTheme();

	const contentArray = textBlockContent || [];
	const isTwoCol = contentArray.length > 1;
	const alignment = alignmentMap[textBlockAlignment || 'Default'] || 'left';

	// Helper to render specific content blocks
	const renderBlock = (index: number) => {
		const block = contentArray[index];
		if (!block?.blockContent) return null;

		const parsed = typeof block.blockContent === 'string' ? JSON.parse(block.blockContent) : block.blockContent;

		const rendererOptions: RendererOptions = {
			variantBody: nullToUndefined(block.variantBody),
			variantHeading1: nullToUndefined(block.variantHeading1),
			variantHeading2: nullToUndefined(block.variantHeading2),
			variantHeading3: nullToUndefined(block.variantHeading3),
			variantHeading4: nullToUndefined(block.variantHeading4),
			variantHeading5: nullToUndefined(block.variantHeading5),
			variantHeading6: nullToUndefined(block.variantHeading6),
			overrides: {
				paragraph: {
					mt: 0,
					fontFamily: '"DIN Next LT Pro"',
					fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)',
					fontWeight: 400,
					lineHeight: 'clamp(1.375rem, 1.2857rem + 0.4464vw, 1.625rem)',
					letterSpacing: '-0.05px',
					[theme.breakpoints.up('md')]: {
						fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
					}
				},
				heading: {
					fontFamily: '"DIN Next LT Pro"',
					mb: 1
				},
				list: {
					pl: 4,
					'& li': {
						p: 0.3,
						fontFamily: '"DIN Next LT Pro"',
						fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
					}
				},
				listItem: {
					fontFamily: '"DIN Next LT Pro"',
					fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)',
					'& .MuiTypography-root': {
						fontSize: 'inherit'
					},
					[theme.breakpoints.up('md')]: {
						fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
					}
				},
				link: {
					textDecoration: 'underline',
					fontFamily: '"DIN Next LT Pro"',
					'&:hover': {
						opacity: 0.8
					},
					[theme.breakpoints.up('md')]: {
						fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
					}
				},
				quote: {
					borderColor: theme.palette.primary.light,
					backgroundColor: 'rgba(255, 255, 255, 0.1)',
					[theme.breakpoints.up('md')]: {
						fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)'
					}
				}
			}
		};

		return <BlocksContentRenderer content={parsed} options={rendererOptions} />;
	};

	const ctaLink = React.useMemo(() => {
		if (!textBlockCta) return undefined;

		if (textBlockCta.hrefExternal) return textBlockCta.hrefExternal;

		if (textBlockCta.href) {
			return buildPathFromRelationLink(textBlockCta.href);
		}

		return undefined;
	}, [textBlockCta]);

	return (
		<Section containerSx={containerSx}>
			<Box className="textBlock" sx={{ overflow: 'hidden' }}>
				<TextBlock
					layout={isTwoCol ? '2-col' : 'full-width'}
					align={alignment}
					title={textBlockHeadline ?? ''}
					bodyLeft={renderBlock(0)}
					bodyRight={isTwoCol ? renderBlock(1) : undefined}
					ctaLabel={textBlockCta?.label ?? undefined}
					ctaHref={ctaLink ?? undefined}
				/>
			</Box>
		</Section>
	);
};

export default ComponentBlocksTextBlock;
